import React, { useEffect, useState } from "react";
import { RefreshCw, Wifi, WifiOff } from "lucide-react";
import toast from "react-hot-toast";
import api from "../api";

export default function Sessions() {
  const [sessions, setSessions] = useState({});
  const [licenses, setLicenses] = useState({});
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [sessRes, licRes] = await Promise.all([
        api.get("/admin/active_sessions"),
        api.get("/admin/list_licenses"),
      ]);
      setSessions(sessRes.data);
      setLicenses(licRes.data);
    } catch (e) {
      toast.error("Gagal load sessions");
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
    const interval = setInterval(load, 10000); // auto-refresh tiap 10s
    return () => clearInterval(interval);
  }, []);

  const idleColor = (secs) => {
    if (secs < 60) return "#4ade80";
    if (secs < 300) return "#f59e0b";
    return "#f87171";
  };

  const formatIdle = (secs) => {
    if (secs < 60) return `${secs}s`;
    if (secs < 3600) return `${Math.floor(secs / 60)}m ${secs % 60}s`;
    return `${Math.floor(secs / 3600)}h`;
  };

  const now = Math.floor(Date.now() / 1000);
  const entries = Object.entries(sessions);

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Active Sessions</h1>
          <p style={styles.sub}>
            {entries.length} sesi aktif — auto-refresh tiap 10 detik
          </p>
        </div>
        <button onClick={load} style={styles.refreshBtn} disabled={loading}>
          <RefreshCw size={15} style={{ animation: loading ? "spin 1s linear infinite" : "none" }} />
          Refresh
        </button>
      </div>

      {entries.length === 0 ? (
        <div style={styles.empty}>
          <WifiOff size={40} color="#334155" />
          <p style={{ color: "#475569", marginTop: 12 }}>Tidak ada sesi aktif saat ini</p>
        </div>
      ) : (
        <div style={styles.grid}>
          {entries.map(([key, sess]) => {
            const idle = now - Math.floor(sess.last_seen || 0);
            const lic = licenses[key] || {};
            const color = idleColor(idle);

            return (
              <div key={key} style={{ ...styles.card, borderLeft: `3px solid ${color}` }}>
                <div style={styles.cardTop}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <Wifi size={16} color={color} />
                    <span style={{ color: "#a5b4fc", fontFamily: "monospace", fontSize: 13 }}>
                      {key}
                    </span>
                  </div>
                  <span style={{
                    padding: "2px 8px", borderRadius: 4, fontSize: 11, fontWeight: 600,
                    background: color + "22", color,
                  }}>
                    idle {formatIdle(idle)}
                  </span>
                </div>

                <div style={styles.cardBody}>
                  <div style={styles.infoRow}>
                    <span style={styles.infoLabel}>HWID</span>
                    <span style={styles.infoVal}>{sess.hwid || "-"}</span>
                  </div>
                  <div style={styles.infoRow}>
                    <span style={styles.infoLabel}>Session Token</span>
                    <span style={{ ...styles.infoVal, fontFamily: "monospace", fontSize: 11 }}>
                      {(sess.session_token || "").slice(0, 18)}...
                    </span>
                  </div>
                  <div style={styles.infoRow}>
                    <span style={styles.infoLabel}>Uses Left</span>
                    <span style={styles.infoVal}>
                      {lic.uses_left === -1 ? "∞" : (lic.uses_left ?? "-")}
                    </span>
                  </div>
                  <div style={styles.infoRow}>
                    <span style={styles.infoLabel}>Note</span>
                    <span style={styles.infoVal}>{lic.note || "-"}</span>
                  </div>
                  <div style={styles.infoRow}>
                    <span style={styles.infoLabel}>Expire</span>
                    <span style={styles.infoVal}>
                      {lic.expire ? lic.expire.slice(0, 10) : "Unlimited"}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const styles = {
  page: { padding: "32px 32px 32px 252px", minHeight: "100vh", fontFamily: "'Segoe UI', sans-serif" },
  header: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 28 },
  title: { color: "#fff", fontSize: 24, fontWeight: 700, margin: 0 },
  sub: { color: "#64748b", fontSize: 13, marginTop: 4 },
  refreshBtn: {
    display: "flex", alignItems: "center", gap: 8,
    padding: "8px 16px", background: "rgba(255,255,255,0.07)",
    border: "1px solid rgba(255,255,255,0.12)", borderRadius: 8,
    color: "#94a3b8", fontSize: 13, cursor: "pointer",
  },
  empty: {
    display: "flex", flexDirection: "column", alignItems: "center",
    justifyContent: "center", minHeight: 300,
  },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))", gap: 16 },
  card: {
    background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: 12, padding: "18px 20px",
  },
  cardTop: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 },
  cardBody: { display: "flex", flexDirection: "column", gap: 8 },
  infoRow: { display: "flex", justifyContent: "space-between", alignItems: "center" },
  infoLabel: { color: "#64748b", fontSize: 12 },
  infoVal: { color: "#e2e8f0", fontSize: 12, maxWidth: 200, textAlign: "right", wordBreak: "break-all" },
};
